import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './TalentfixrJobs.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({ regions: [], categories: [] });
  const [activeRegion, setActiveRegion] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/jobs`).then(r => r.json()),
      fetch(`${API}/jobs/filters`).then(r => r.json()),
    ]).then(([jobsData, filtersData]) => {
      setJobs(jobsData.jobs || []);
      setFilters(filtersData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = jobs.filter(j => {
    if (activeRegion && j.region !== activeRegion) return false;
    return true;
  });

  return (
    <div className="tf-jobs" data-testid="jobs-page">
      {/* Header */}
      <div className="tf-container" style={{ paddingBottom: 0 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 12 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', minWidth: 0 }}>
            <img src="/assets/logo.png" alt="TalentFixr" style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
            <span style={{ fontSize: 20, fontWeight: 800, color: '#163e32', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>TALENTFIXR.</span>
          </Link>
          <NavPills active="jobs" />
        </header>
      </div>

      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '32px 24px 24px', maxWidth: 700, margin: '0 auto' }}>
        <p style={{ fontSize: 13, color: '#637163', marginBottom: 10, letterSpacing: 0.3 }}>
          Voor techniekers. Zonder cv.
        </p>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: '#163e32', lineHeight: 1.15, marginBottom: 14, letterSpacing: -0.5 }}>
          Goed in je vak.<br />Toe aan een betere job?
        </h1>
        <p style={{ fontSize: 15, color: '#566b60', lineHeight: 1.6, maxWidth: 520, margin: '0 auto' }}>
          Bekijk meteen je loon, je regio en je werkplek. Jij kiest wat past.
        </p>
      </section>

      {/* Filters */}
      {filters.regions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', padding: '0 24px 28px', maxWidth: 900, margin: '0 auto' }}>
          <FilterBtn label="Alle jobs" active={!activeRegion} onClick={() => setActiveRegion('')} testId="filter-all" />
          {filters.regions.map(r => (
            <FilterBtn key={r} label={r} active={activeRegion === r} onClick={() => setActiveRegion(activeRegion === r ? '' : r)} testId={`filter-region-${r}`} />
          ))}
        </div>
      )}

      {/* Cards Grid */}
      <div className="tf-container" style={{ paddingTop: 0 }}>
        <div className="tf-grid">
          {loading ? (
            <p style={{ textAlign: 'center', color: '#637163', gridColumn: '1 / -1', padding: 40 }}>Laden...</p>
          ) : filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#637163', gridColumn: '1 / -1', padding: 40 }}>Geen vacatures gevonden.</p>
          ) : (
            filtered.map(job => <JobCard key={job.id} job={job} />)
          )}
        </div>
      </div>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '32px 20px 20px', fontSize: 12, color: '#637163', borderTop: '1px solid #d9ddce', marginTop: 48, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
        <Link to="/privacy" style={{ color: '#637163' }}>Privacybeleid</Link>
        <Link to="/terms" style={{ color: '#637163' }}>Algemene voorwaarden</Link>
        <span>&copy; 2026 TALENTFIXR.</span>
      </footer>
    </div>
  );
};

const FilterBtn = ({ label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={{
      padding: '8px 18px', borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer',
      border: `1px solid ${active ? '#163e32' : '#aebcad'}`,
      background: active ? '#163e32' : 'transparent',
      color: active ? '#f5f0e6' : '#163e32',
      fontFamily: 'Arial, sans-serif',
    }}
  >
    {label}
  </button>
);

const JobCard = ({ job }) => {
  const badges = [job.hours, ...(job.features || [])].filter(Boolean);

  return (
    <Link to={`/jobs/${job.slug}`} className="tf-job-card" data-testid={`job-card-${job.slug}`}>
      {/* Green banner */}
      <div className="tf-card-banner">
        <h3>{job.title}</h3>
        <p>{job.location}</p>
      </div>

      {/* Badges */}
      <div className="tf-tags" style={{ marginTop: 0, marginBottom: 12 }}>
        {badges.map((b, i) => <span className="tf-tag" key={i}>{b}</span>)}
      </div>

      {/* Employer */}
      {job.employer_name && (
        <p style={{ fontSize: 13, color: '#637163', marginBottom: 8 }}>{job.employer_name}</p>
      )}

      {/* Intro */}
      <p style={{ fontSize: 14, color: '#566b60', lineHeight: 1.5, marginBottom: 0 }}>
        {job.intro}
      </p>

      {/* CTA */}
      <span className="tf-card-action">
        Bekijk deze job <span className="tf-arrow">&rarr;</span>
      </span>
    </Link>
  );
};

const NavPills = ({ active }) => {
  const pillRef = useRef(null);
  const schrijfRef = useRef(null);
  const jobsRef = useRef(null);
  const [slider, setSlider] = useState({ left: 0, width: 0 });

  const measure = useCallback(() => {
    const container = pillRef.current;
    const target = active === 'schrijf' ? schrijfRef.current : jobsRef.current;
    if (!container || !target) return;
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    setSlider({ left: tRect.left - cRect.left, width: tRect.width });
  }, [active]);

  useEffect(() => { measure(); window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }, [measure]);

  return (
    <nav ref={pillRef} style={{ display: 'flex', position: 'relative', background: 'rgba(22,62,50,0.08)', borderRadius: 100, padding: 3, flexShrink: 0 }}>
      <div style={{
        position: 'absolute', top: 3, height: 'calc(100% - 6px)',
        background: '#163e32', borderRadius: 100, pointerEvents: 'none',
        transition: 'left 0.3s cubic-bezier(0.16,1,0.3,1), width 0.3s cubic-bezier(0.16,1,0.3,1)',
        left: slider.left, width: slider.width,
      }} />
      <Link ref={schrijfRef} to="/" style={{
        position: 'relative', zIndex: 1, padding: '7px 14px', fontSize: 12, fontWeight: 600,
        borderRadius: 100, textDecoration: 'none', whiteSpace: 'nowrap',
        color: active === 'schrijf' ? '#f5f0e6' : '#163e32', transition: 'color 0.2s',
      }} data-testid="header-register-btn">Schrijf je in</Link>
      <Link ref={jobsRef} to="/jobs" style={{
        position: 'relative', zIndex: 1, padding: '7px 14px', fontSize: 12, fontWeight: 600,
        borderRadius: 100, textDecoration: 'none', whiteSpace: 'nowrap',
        color: active === 'jobs' ? '#f5f0e6' : '#163e32', transition: 'color 0.2s',
      }} data-testid="nav-jobs">JOBS</Link>
    </nav>
  );
};

export default JobsPage;
